"use client";

import {
  createContext,
  useCallback,
  useContext,
  useId,
  useMemo,
  useRef,
  useState,
  type FormHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import type { ZodError, ZodTypeAny, infer as ZodInfer } from "zod";
import {
  getFormsCopy,
  translateApiErrorMessage,
  translateValidationMessage,
} from "@/content/forms";
import { ApiError } from "@/lib/api/client";
import type { Locale } from "@/lib/i18n/config";
import { useLocale } from "@/lib/i18n/provider";
import { track } from "@/lib/analytics";

/**
 * Generic, accessible form primitive.
 *
 * - Validates with a zod schema before calling `onSubmit`.
 * - Tracks lifecycle (submitting / success / error) and exposes it via
 *   context so success / error states can render inline.
 * - Cancels in-flight submissions via AbortController on unmount or
 *   re-submit.
 * - Exposes `aria-live` regions for both errors and successes.
 *
 * The four marketing forms compose this — they only declare schema +
 * submit handler + fields.
 */

type Status = "idle" | "submitting" | "success" | "error";

type FieldErrors = Record<string, string | undefined>;

type FormCtx = {
  formId: string;
  status: Status;
  errors: FieldErrors;
  rootError?: string;
  successMessage?: string;
  thankYouHref?: string;
  thankYouHrefLabel?: string;
};

const SUBMIT_TIMEOUT_MS = 12000;

const Ctx = createContext<FormCtx | null>(null);

function useFormCtx(): FormCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("Form sub-component used outside <Form>");
  return ctx;
}

type FormProps<S extends ZodTypeAny> = {
  schema: S;
  onSubmit: (
    values: ZodInfer<S>,
    signal: AbortSignal,
  ) => Promise<string | void>;
  successMessage?: string;
  thankYouHref?: string;
  thankYouHrefLabel?: string;
  trackingName: string;
  children: ReactNode;
  className?: string;
} & Omit<FormHTMLAttributes<HTMLFormElement>, "onSubmit" | "children">;

export function Form<S extends ZodTypeAny>({
  schema,
  onSubmit,
  successMessage,
  thankYouHref,
  thankYouHrefLabel,
  trackingName,
  children,
  className = "",
  ...rest
}: FormProps<S>) {
  const { locale } = useLocale();
  const forms = getFormsCopy(locale);

  const formId = useId();
  const [status, setStatus] = useState<Status>("idle");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [rootError, setRootError] = useState<string | undefined>(undefined);
  const [resolvedSuccess, setResolvedSuccess] = useState<string | undefined>(
    successMessage,
  );
  const abortRef = useRef<AbortController | null>(null);
  /** Sync guard — React state alone cannot block rapid double-submit. */
  const submittingRef = useRef(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (submittingRef.current) {
        return;
      }
      const formEl = e.currentTarget;
      /*
       * `FormData` serialises empty optional fields as "" which zod
       * treats as a present string, so `.optional()` rejects them on
       * min-length rules. Strip empty strings here so "absent"
       * semantics flow through to the schema.
       */
      const raw = Object.fromEntries(new FormData(formEl).entries());
      const data: Record<string, FormDataEntryValue> = {};
      for (const [k, v] of Object.entries(raw)) {
        if (typeof v === "string" && v.trim() === "") continue;
        data[k] = v;
      }
      const parsed = schema.safeParse(data);
      if (!parsed.success) {
        setErrors(zodIssuesToFieldErrors(parsed.error, locale));
        setRootError(forms.primitive.checkFieldsError);
        setStatus("error");
        return;
      }

      setErrors({});
      setRootError(undefined);
      setStatus("submitting");
      submittingRef.current = true;
      track("form_submit_started", { form: trackingName });

      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      let timeoutId: ReturnType<typeof setTimeout> | undefined;

      try {
        const overrideSuccess = await Promise.race<string | void>([
          onSubmit(parsed.data, ctrl.signal),
          new Promise<never>((_, reject) => {
            timeoutId = setTimeout(() => {
              ctrl.abort();
              reject(
                new ApiError({
                  status: 0,
                  code: "submission_timeout",
                  message: forms.primitive.genericError,
                }),
              );
            }, SUBMIT_TIMEOUT_MS);
          }),
        ]);
        if (ctrl.signal.aborted) return;
        if (overrideSuccess) setResolvedSuccess(overrideSuccess);
        else setResolvedSuccess(successMessage);
        setStatus("success");
        track("form_submit_succeeded", { form: trackingName });
        formEl.reset();
      } catch (err) {
        if (ctrl.signal.aborted && !(err instanceof ApiError && err.code === "submission_timeout")) {
          return;
        }
        const message =
          err instanceof ApiError
            ? translateApiErrorMessage(err.code, err.message, locale)
            : forms.primitive.genericError;
        setRootError(message);
        setStatus("error");
        track("form_submit_failed", {
          form: trackingName,
          code: err instanceof ApiError ? err.code : "unknown",
        });
      } finally {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        submittingRef.current = false;
      }
    },
    [schema, onSubmit, successMessage, trackingName, locale, forms],
  );

  const ctx = useMemo<FormCtx>(
    () => ({
      formId,
      status,
      errors,
      rootError,
      successMessage: resolvedSuccess,
      thankYouHref,
      thankYouHrefLabel,
    }),
    [formId, status, errors, rootError, resolvedSuccess, thankYouHref, thankYouHrefLabel],
  );

  return (
    <Ctx.Provider value={ctx}>
      <form
        {...rest}
        onSubmit={handleSubmit}
        className={className}
        noValidate
        aria-busy={status === "submitting"}
      >
        {status === "success" ? <FormThankYou /> : children}
      </form>
    </Ctx.Provider>
  );
}

function zodIssuesToFieldErrors(error: ZodError, locale: Locale): FieldErrors {
  const out: FieldErrors = {};
  for (const issue of error.issues) {
    const key = issue.path[0];
    if (typeof key === "string" && !out[key]) {
      out[key] = translateValidationMessage(issue.message, locale);
    }
  }
  return out;
}

/* ─── Field primitives ──────────────────────────────────────────── */

type LabelProps = {
  name: string;
  label: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
};

function FieldShell({ name, label, hint, required, children, className = "" }: LabelProps) {
  const { formId, errors } = useFormCtx();
  const id = `${formId}-${name}`;
  const errId = `${id}-err`;
  const hintId = `${id}-hint`;
  const error = errors[name];
  const describedBy =
    [error ? errId : undefined, hint ? hintId : undefined]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <div className={`flex flex-col gap-1.5 ${className}`.trim()}>
      <label
        htmlFor={id}
        className="text-[12px] font-medium uppercase tracking-[0.14em] text-[var(--color-text-muted)]"
      >
        {label}
        {required && <span aria-hidden className="ml-1 text-[var(--color-accent-base)]">*</span>}
      </label>
      {/* Children get the resolved id + describedBy via cloning below */}
      {wireChild(children, { id, name, "aria-describedby": describedBy, "aria-invalid": !!error })}
      {hint && !error && (
        <p id={hintId} className="text-[12px] text-[var(--color-text-muted)]">
          {hint}
        </p>
      )}
      {error && (
        <p id={errId} role="alert" className="text-[12px] text-[var(--color-state-danger)]">
          {error}
        </p>
      )}
    </div>
  );
}

/* eslint-disable @typescript-eslint/no-explicit-any */
function wireChild(child: ReactNode, props: Record<string, unknown>): ReactNode {
  if (!child || typeof child !== "object" || !("props" in (child as any))) return child;
  const c = child as any;
  return {
    ...c,
    props: { ...c.props, ...props },
  };
}
/* eslint-enable @typescript-eslint/no-explicit-any */

const inputBase =
  "h-12 w-full rounded-2xl bg-[var(--color-surface-glass)] px-4 text-[15px] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] border border-[var(--color-surface-border)] transition-colors focus:border-[var(--color-accent-base)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-base)]/40 aria-[invalid=true]:border-[var(--color-state-danger)]";

type InputProps = {
  name: string;
  label: string;
  hint?: string;
  required?: boolean;
  className?: string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "name">;

export function FormInput({ name, label, hint, required, className, ...rest }: InputProps) {
  return (
    <FieldShell name={name} label={label} hint={hint} required={required} className={className}>
      <input
        type={rest.type ?? "text"}
        name={name}
        required={required}
        className={inputBase}
        {...rest}
      />
    </FieldShell>
  );
}

type TextareaProps = {
  name: string;
  label: string;
  hint?: string;
  required?: boolean;
  className?: string;
} & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "name">;

export function FormTextarea({ name, label, hint, required, className, rows = 4, ...rest }: TextareaProps) {
  return (
    <FieldShell name={name} label={label} hint={hint} required={required} className={className}>
      <textarea
        name={name}
        required={required}
        rows={rows}
        className={`${inputBase} h-auto py-3`}
        {...rest}
      />
    </FieldShell>
  );
}

type SelectProps = {
  name: string;
  label: string;
  hint?: string;
  required?: boolean;
  options: { value: string; label: string }[];
  className?: string;
} & Omit<SelectHTMLAttributes<HTMLSelectElement>, "name">;

export function FormSelect({ name, label, hint, required, options, className, ...rest }: SelectProps) {
  const { locale } = useLocale();
  const forms = getFormsCopy(locale);

  return (
    <FieldShell name={name} label={label} hint={hint} required={required} className={className}>
      <select
        name={name}
        required={required}
        className={inputBase}
        defaultValue=""
        {...rest}
      >
        <option value="" disabled>
          {forms.primitive.selectOneLabel}
        </option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}

type CheckboxProps = {
  name: string;
  label: ReactNode;
  hint?: string;
  required?: boolean;
  className?: string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "name" | "type">;

export function FormCheckbox({
  name,
  label,
  hint,
  required,
  className,
  ...rest
}: CheckboxProps) {
  const { formId, errors } = useFormCtx();
  const id = `${formId}-${name}`;
  const errId = `${id}-err`;
  const hintId = `${id}-hint`;
  const error = errors[name];
  const describedBy =
    [error ? errId : undefined, hint ? hintId : undefined].filter(Boolean).join(" ") ||
    undefined;

  return (
    <div className={`flex flex-col gap-1.5 ${className ?? ""}`.trim()}>
      <label
        htmlFor={id}
        className="flex cursor-pointer items-start gap-2.5 text-[13px] leading-relaxed text-[var(--color-text-secondary)]"
      >
        <input
          id={id}
          name={name}
          type="checkbox"
          required={required}
          aria-describedby={describedBy}
          aria-invalid={!!error}
          className="mt-[2px] h-4 w-4 shrink-0 rounded border border-[var(--color-surface-border-strong)] bg-[var(--color-surface-glass)] text-[var(--color-accent-base)] accent-[var(--color-accent-base)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent-base)] aria-[invalid=true]:border-[var(--color-state-danger)]"
          {...rest}
        />
        <span>{label}</span>
      </label>
      {hint && !error && (
        <p id={hintId} className="text-[12px] text-[var(--color-text-muted)]">
          {hint}
        </p>
      )}
      {error && (
        <p id={errId} role="alert" className="text-[12px] text-[var(--color-state-danger)]">
          {error}
        </p>
      )}
    </div>
  );
}

/* ─── Status regions ────────────────────────────────────────────── */

function FormThankYou() {
  const { locale } = useLocale();
  const forms = getFormsCopy(locale);
  const { successMessage, thankYouHref, thankYouHrefLabel } = useFormCtx();

  return (
    <div
      role="status"
      aria-live="polite"
      className="rounded-2xl border border-[var(--color-surface-border)] bg-[var(--color-bg-raised)] px-5 py-5"
    >
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--color-accent-base)]">
        {forms.primitive.thankYouEyebrow}
      </p>
      <p className="mt-2 text-[15px] leading-relaxed text-[var(--color-text-primary)]">
        {successMessage ?? forms.primitive.defaultDone}
      </p>
      {thankYouHref && thankYouHrefLabel ? (
        <a
          href={thankYouHref}
          className="mt-4 inline-flex text-[13px] font-semibold text-[var(--color-accent-base)] underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-accent-base)]"
        >
          {thankYouHrefLabel}
        </a>
      ) : null}
    </div>
  );
}

export function FormStatus({ className = "" }: { className?: string }) {
  const { status, rootError } = useFormCtx();

  // Success UI is rendered by FormThankYou (replaces fields). Keep error region here.
  if (status === "success") {
    return null;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className={`min-h-[1.25rem] text-[13px] ${className}`.trim()}
    >
      {status === "error" && rootError && (
        <span className="text-[var(--color-state-danger)]">{rootError}</span>
      )}
    </div>
  );
}

export function useFormStatus(): Status {
  return useFormCtx().status;
}
